<template>
  <div class="app-header"></div>
  <v-progress-linear v-if="isRouteLoading" class="route-progress" indeterminate color="primary" height="3"/>
  <router-view :key="fullPath"></router-view>
  <chat-widget />
  <app-snackbar />
  <post-sign-promo-host />
  <re-captcha />
  <!-- Окно «Заказать звонок»: кнопка в SiteFooter только открывает его через общий стор ui-kit. -->
  <kt-callback />
</template>

<script setup lang="ts">
import {watch, provide, onMounted} from "vue";
import {useRoute} from "vue-router";
import {useI18n} from '@/shared/lib/i18n';
import {useHead} from "@vueuse/head";
import {KtCallback, ReCaptcha} from '@ismet/vue-ui-kit'

import {UserModel} from '@/entities/User';

import {PostSignPromoHost} from '@/features/PostSignPromo';

import ChatWidget from '@/widgets/ChatWidget';
import AppSnackbar from '@/shared/ui/AppSnackbar.vue';

import {AppPages, AppRoutes, EAppProviders, isRouteLoading} from "@/app/providers";
import {isTelecomProd, isProd} from "@/shared/lib/utils/environment";

const {locale, t} = useI18n();

const {fullPath} = useRoute();

useHead({
  title: () => t('seo.title'),
  meta: [
    {name: 'description', content: () => t('seo.description')},
    {property: 'og:title', content: () => t('seo.title')},
    {property: 'og:description', content: () => t('seo.description')},
  ],
});

const user = UserModel.useUserStore();

// Эффект один на приложение: догоняет язык на бэке, когда профиль пришёл с другим
UserModel.useProfileLocaleSync();

provide(EAppProviders.AppRoutes, AppRoutes);
provide(EAppProviders.AppPages, AppPages);

// immediate — язык мог прийти из URL (/kk, ?lang=kk) или cookie, тогда watch без него не сработает
watch(locale, (val) => {
  document.documentElement.lang = val;
}, {immediate: true});

if (!isTelecomProd() && !isProd()) {
  const meta = document.createElement('meta');
  meta.name = 'google-site-verification';
  meta.content = 'uTNKcRws_L6-z_GEdPHBA61P8n1woyFCJOCNnsy0EH4';
  document.head.appendChild(meta);
}

onMounted(async () => {
  try {
    if (UserModel.hasSessionCookie()) {
      await user.getUserData();
    }
  } finally {
    // Даже если сессию поднять не удалось, ожидание закончено: гость — это гость.
    user.markSessionResolved();
  }
});
</script>

<style>
/* Страницы приезжают отдельными чанками, и router-view пересоздаётся по :key,
   поэтому на время перехода экран пуст. Полоса показывает, что переход идёт. */
.route-progress {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2500;
}

/* Бейдж невидимой reCAPTCHA не нужен в интерфейсе. Только visibility:
   display:none отключает саму капчу, и токен перестаёт приходить. */
.grecaptcha-badge {
  visibility: hidden;
}
</style>
